import { useCookies } from '@/app/cookie-provider';
import BadgeStoreFront from '@/components/badge-storefront';
import { ElementProps } from '@/components/sections/builder/types';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { dummyCarts } from '@/constant/dummyData/dummy-carts';
import { useMutateValidatePromotion } from '@/hooks/api/storefront/promotion';
import { deleteCartItem, updateCartItem } from '@/lib/actions/cart/action';
import { SendEventAnalytic, SendEventMetapixel } from '@/lib/analytic-send-event';
import { cn, formatIDR, formatNumber } from '@/lib/utils';
import { ThemeType, useBuilderStore } from '@/stores/builder';
import { usePromotionStore } from '@/stores/promotion';
import { HttpTypes } from '@/types';
import { StoreCart } from '@/types/http';
import { useQueryClient } from '@tanstack/react-query';
import { BadgeDollarSign, CircleAlert, MinusIcon, PlusIcon, Trash2 } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useRef, useState } from 'react';
import { shallow } from 'zustand/shallow';
import Cookies from 'js-cookie';
import { AxiosError } from 'axios';
import { BasePromotion } from '@/types/http/promotion/common';
import useModal from '@/hooks/useModal';
import ModalAlert from '@/components/sections/modal-alert';

export default function ElementContent({
  data,
  host,
  preview,
  theme,
}: ElementProps<HttpTypes.StoreCart>) {
  const { multiOrigin } = useCookies();
  const [isLoading, setIsLoading] = useState<{ [key: string]: boolean }>({});
  const [currentItemQuantities, setCurrentItemQuantities] = useState<{ [key: string]: number }>({});
  const queryClient = useQueryClient();
  const token = Cookies.get('accessToken');
  const modalConfirmSpecific = useModal<{
    onConfirm: () => void;
    onCancel?: () => void;
  }>();

  const carts = preview ? dummyCarts : data;
  const cartItems = carts?.items || [];
  const isEmptyCart = cartItems.length === 0;

  const builder = useBuilderStore(
    (state) => ({
      theme: state.theme,
    }),
    shallow
  );
  const themeContent = preview ? builder.theme : theme;

  const { selectedPromotion, removePromotion, replacePromotion } = usePromotionStore(
    (state) => ({
      selectedPromotion: state.selectedPromotion,
      removePromotion: state.removePromotion,
      replacePromotion: state.replacePromotion,
    }),
    shallow
  );

  const validatePromotion = useMutateValidatePromotion({
    onSuccess: (data) => {
      const isValid = data.data.is_valid;
      const promoRewards = data.data.promotion_rewards;
      if (isValid && promoRewards) {
        promoRewards.forEach((promo: BasePromotion) => {
          replacePromotion(promo);
        });
      }
    },
  });

  const getUpdatedItems = (quantities: typeof currentItemQuantities, items: typeof cartItems) => {
    return items.map((item) => ({
      ...item,
      quantity: quantities[item.id] ?? item.quantity,
    }));
  };

  const tryValidatePromotion = async (customItems: typeof cartItems) => {
    const promotions = selectedPromotion
      .filter((promo) => promo.product_rules_type === 'specific' || promo.use_voucher)
      .map((promo) => ({
        promotion_id: promo.promotion_id,
        voucher_code: promo.voucher_code ?? '',
      }));

    if (promotions.length === 0) return;

    const items = customItems.map((item) => {
      return {
        item_id: item.id,
        qty: item.quantity,
        amount: Number(item.sell_price),
      };
    });

    const payload = {
      items,
      promotions,
      grand_total: items.reduce((sum, item) => sum + item.amount * item.qty, 0),
    };

    await validatePromotion.mutateAsync({
      body: payload,
      token: token as string,
    });
  };

  const debounceTimers = useRef<{ [key: string]: NodeJS.Timeout }>({});

  const processQtyChange = async (item: StoreCart, qty: number) => {
    setIsLoading((prev) => ({ ...prev, [item.id]: true }));

    const updatedQuantities = {
      ...currentItemQuantities,
      [item.id]: qty,
    };

    const updatedItems = getUpdatedItems(updatedQuantities, cartItems);

    try {
      await tryValidatePromotion(updatedItems);
      setCurrentItemQuantities(updatedQuantities);
      await updateCartItem(host as string, { ...item, quantity: qty });
      queryClient.invalidateQueries({ queryKey: ['carts'] });
      setIsLoading((prev) => ({ ...prev, [item.id]: false }));
    } catch (error) {
      if (error instanceof AxiosError) {
        const message = JSON.parse(error.response?.data.message);
        if (message?.promotion_id) {
          modalConfirmSpecific.open({
            onConfirm: async () => {
              removePromotion(message.promotion_id);
              modalConfirmSpecific.close();
              setCurrentItemQuantities(updatedQuantities);
              await updateCartItem(host as string, { ...item, quantity: qty });
              queryClient.invalidateQueries({ queryKey: ['carts'] });
              setIsLoading((prev) => ({ ...prev, [item.id]: false }));
            },
            onCancel: () => {
              setIsLoading((prev) => ({ ...prev, [item.id]: false }));
              modalConfirmSpecific.close();
            },
          });
        } else {
          setIsLoading((prev) => ({ ...prev, [item.id]: false }));
        }
      } else {
        setIsLoading((prev) => ({ ...prev, [item.id]: false }));
      }
    }
  };

  const debouncedQtyChange = (item: StoreCart, qty: number) => {
    setCurrentItemQuantities((prev) => ({
      ...prev,
      [item.id]: qty,
    }));

    if (debounceTimers.current[item.id]) {
      clearTimeout(debounceTimers.current[item.id]);
    }

    debounceTimers.current[item.id] = setTimeout(() => {
      processQtyChange(item, qty);
    }, 1000);
  };

  const handleIncrementQty = (item: StoreCart, idx: number = 0) => {
    const newQty = (currentItemQuantities[item.id] || item.quantity) + 1;
    if (newQty > (data?.item_stock?.[idx]?.stock || 0)) return;
    debouncedQtyChange(item, newQty);
  };

  const handleDecrementQty = (item: StoreCart) => {
    const newQty = Math.max(1, (currentItemQuantities[item.id] || item.quantity) - 1);
    debouncedQtyChange(item, newQty);
  };

  const removeItem = async (item: StoreCart) => {
    setIsLoading((prev) => ({ ...prev, [item.id]: true }));

    const updatedItems = getUpdatedItems(
      currentItemQuantities,
      cartItems.filter((i) => i.id !== item.id)
    );

    try {
      await tryValidatePromotion(updatedItems);
      await deleteCartItem(host as string, item.id);
      queryClient.invalidateQueries({ queryKey: ['carts'] });

      // Send analytics events
      SendEventAnalytic('remove_from_cart', {
        currency: 'IDR',
        value: Number(item.sell_price) * item.quantity,
        items: [
          {
            item_name: item?.product_name,
            item_id: item.id as string,
            price: Number(item?.sell_price),
            quantity: item.quantity,
            unique_action_id: Date.now(),
          },
        ],
      });
      SendEventMetapixel('RemoveFromCart', {
        content_ids: [item.id],
        content_name: item.product_name,
        content_type: 'product',
        quantity: item.quantity,
        value: Number(item.sell_price) * item.quantity,
        currency: 'IDR',
      });

      setIsLoading((prev) => ({ ...prev, [item.id]: false }));
    } catch (error) {
      if (error instanceof AxiosError) {
        const message = JSON.parse(error.response?.data.message);
        if (message?.promotion_id) {
          modalConfirmSpecific.open({
            onConfirm: async () => {
              removePromotion(message.promotion_id);
              modalConfirmSpecific.close();

              await deleteCartItem(host as string, item.id);
              queryClient.invalidateQueries({ queryKey: ['carts'] });

              // Send analytics events
              SendEventAnalytic('remove_from_cart', {
                currency: 'IDR',
                value: Number(item.sell_price) * item.quantity,
                items: [
                  {
                    item_name: item?.product_name,
                    item_id: item.id as string,
                    price: Number(item?.sell_price),
                    quantity: item.quantity,
                    unique_action_id: Date.now(),
                  },
                ],
              });
              SendEventMetapixel('RemoveFromCart', {
                content_ids: [item.id],
                content_name: item.product_name,
                content_type: 'product',
                quantity: item.quantity,
                value: Number(item.sell_price) * item.quantity,
                currency: 'IDR',
              });

              setIsLoading((prev) => ({ ...prev, [item.id]: false }));
            },
            onCancel: () => {
              setIsLoading((prev) => ({ ...prev, [item.id]: false }));
              modalConfirmSpecific.close();
            },
          });
        } else {
          setIsLoading((prev) => ({ ...prev, [item.id]: false }));
        }
      } else {
        setIsLoading((prev) => ({ ...prev, [item.id]: false }));
      }
    }
  };

  const handleOutOfStock = (idx: number) => {
    return data?.item_stock?.[idx]?.stock === 0;
  };

  const checkStockAfterChangeOrigin = (idx: number, quantity: number): boolean => {
    const stockValue = data?.item_stock?.[idx]?.stock;
    const stock = Number(stockValue) || 0;
    return quantity > stock;
  };

  const handleDisableAction = (idx: number, quantity: number): boolean => {
    const stockValue = data?.item_stock?.[idx]?.stock;
    const stock = Number(stockValue) || 0;
    return quantity >= stock;
  };
  return (
    <>
      <section className='theme_cart_page_bg py-10'>
        <div
          className={cn(
            'mx-auto px-6 duration-100',
            (themeContent?.layout?.pageWidth ?? 0) < 800
              ? 'theme_layout_page_width'
              : 'max-w-[1000px]'
          )}
        >
          {isEmptyCart ? (
            <div className='flex min-h-[calc(100vh-40rem)] items-center justify-center lg:min-h-[calc(100vh-40rem)]'>
              <h1 className='theme_cart_page_color text-lg md:text-3xl'>Your cart is empty</h1>
            </div>
          ) : (
            <div className='theme_cart_page_color mb-4 flex items-center justify-between'>
              <h1 className='text-lg md:text-3xl'>Your cart</h1>
              <Link href='/' className='text-sm underline md:text-base'>
                {themeContent?.cart?.labelContinueShopping || 'Continue shopping'}
              </Link>
            </div>
          )}
          <div>
            {cartItems
              .sort((a, b) => a.product_name.localeCompare(b.product_name))
              .map((item, idx) => {
                const images = (item.product_assets || []).filter(
                  (asset) => asset.assets_type === 'image'
                );
                const displayedQuantity = currentItemQuantities[item.id] || item.quantity;

                const totalPrice =
                  displayedQuantity *
                  (item.discount_price && item.pricebook_id === null
                    ? item.discount_price
                    : item.sell_price);

                // (item.discount_price ? item.discount_price : item.sell_price);
                const isOutOfStock = handleOutOfStock(idx || 0);
                const disable = handleDisableAction(idx || 0, item.quantity);
                const showNotifyStock = checkStockAfterChangeOrigin(idx || 0, item.quantity);
                const stock = Number(data?.item_stock?.[idx]?.stock);
                const suggestionStock = (idx: number) => {
                  return data?.item_stock?.[idx]?.available_stock || [];
                };

                const isUsedPricebook = data?.items?.[idx]?.pricebook_id !== null;

                const suggestionPricebook = (idx: number) => {
                  return data?.items?.[idx]?.pricebook || [];
                };

                const isAnyPricebook = (data?.items?.[idx]?.pricebook ?? []).length > 0;

                return (
                  <div className='space-y-4 border-b py-6' key={item.id}>
                    <div className='w-full'>
                      <div className='flex gap-x-2'>
                        <Image
                          src={images[0]?.assets_url}
                          alt={item.product_name}
                          width={150}
                          height={150}
                          className={cn('h-[150px] w-[150px] rounded object-cover', {
                            '!grayscale': isOutOfStock,
                          })}
                        />

                        <div className='w-full max-w-full flex-col space-y-1 overflow-hidden px-2'>
                          <div className={cn('w-fit', { '!grayscale': isOutOfStock })}>
                            <BadgeStoreFront theme={theme as ThemeType} item={item} />
                          </div>
                          <div className='flex flex-col'>
                            <h2
                              className={cn(
                                'theme_cart_page_color truncate break-all text-base md:break-words',
                                isOutOfStock && 'text-gray-500'
                              )}
                            >
                              {item.product_name}
                            </h2>
                            {item.variants?.map((variant) => (
                              <p
                                className='truncate break-all md:break-words'
                                key={variant.option_id}
                              >
                                {variant.label}
                              </p>
                            ))}
                          </div>
                          <div
                            className={cn(
                              item?.discount_price && 'flex flex-col items-start gap-1',
                              isOutOfStock && 'text-gray-500'
                            )}
                          >
                            <span
                              className={cn(
                                ' text-base font-bold',
                                isOutOfStock ? 'text-gray-500' : 'text_product_secondary'
                              )}
                            >
                              {formatIDR(
                                item?.discount_price && item.pricebook_id === null
                                  ? item?.discount_price
                                  : item?.sell_price || 0
                              )}
                            </span>

                            {(item?.discount_price as number) > 0 && !isUsedPricebook ? (
                              <span
                                className={cn(
                                  'text-sm text-gray-500 line-through',
                                  isOutOfStock && 'text-gray-500'
                                )}
                              >
                                {formatIDR(item?.sell_price || 0)}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className='theme_cart_page_color flex justify-end'>
                      <div className='flex items-center gap-x-4 gap-y-2'>
                        <div
                          className={cn(
                            'text-right text-sm md:text-base',
                            isOutOfStock ? 'text-gray-500' : 'theme_cart_page_color'
                          )}
                        >
                          {formatIDR(totalPrice)}
                        </div>
                        {multiOrigin && (
                          <DropdownMenu>
                            <DropdownMenuTrigger className='flex items-center focus:border-none focus:outline-none'>
                              <CircleAlert className='h-4 w-4' />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align='center' className='z-[1000] w-[200px] '>
                              <DropdownMenuLabel>See Where It’s Available</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuGroup className='max-h-[200px] overflow-y-auto'>
                                {suggestionStock(idx).map((stockItem, index) => (
                                  <DropdownMenuItem
                                    key={index}
                                    className='flex justify-between gap-x-3'
                                  >
                                    <span>{stockItem.location_name}</span>
                                    <span>{stockItem.quantity}</span>
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuGroup>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}

                        {isAnyPricebook && isUsedPricebook && (
                          <DropdownMenu>
                            <DropdownMenuTrigger className='z-10 flex cursor-pointer items-center focus:border-none focus:outline-none'>
                              <BadgeDollarSign className='h-4 w-4' />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align='center' className='z-[1000] w-[250px] '>
                              <DropdownMenuLabel className='text-sm'>
                                Tiered Pricing
                              </DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuGroup className='max-h-[200px] overflow-y-auto'>
                                {suggestionPricebook(idx).map((item, index) => {
                                  const label = `Buy ${
                                    item.min_unit === 1 ? '1' : `> ${(item?.min_unit ?? 0) - 1}`
                                  } for ${formatIDR(item.retail_price ?? 0)}/unit`;
                                  return (
                                    <DropdownMenuItem
                                      key={index}
                                      className='flex justify-between gap-x-3 text-xs'
                                    >
                                      <span>{label}</span>
                                    </DropdownMenuItem>
                                  );
                                })}
                              </DropdownMenuGroup>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                        <button onClick={() => removeItem(item)} disabled={isLoading[item.id]}>
                          <Trash2 className='h-4 w-4' />
                        </button>
                        <div className='theme_cart_page_color flex h-10 w-fit items-center rounded border'>
                          <button
                            onClick={() => handleDecrementQty(item)}
                            className={`h-full px-3 ${
                              displayedQuantity <= 1 ? 'cursor-not-allowed' : 'cursor-pointer'
                            }`}
                            disabled={displayedQuantity <= 1}
                          >
                            <MinusIcon className='h-4 w-4 cursor-pointer ' />
                          </button>
                          <span className='mx-6'>{formatNumber(displayedQuantity)}</span>
                          <button
                            disabled={disable}
                            onClick={() => handleIncrementQty(item, idx)}
                            className={cn('h-full px-3')}
                          >
                            <PlusIcon
                              className={cn(
                                'h-4 w-4  ',
                                disable ? 'cursor-not-allowed' : 'cursor-pointer'
                              )}
                            />
                          </button>
                        </div>
                      </div>
                      {multiOrigin && (
                        <div className='flex flex-row-reverse gap-x-2 gap-y-2 text-center md:w-full lg:flex-row'>
                          {multiOrigin &&
                            ((stock === 0 && isOutOfStock) || (stock > 0 && showNotifyStock)) && (
                              <Badge variant='destructive'>
                                <span className='text-[10px]'>
                                  {stock === 0 && isOutOfStock
                                    ? 'Stock Not Available'
                                    : 'Stock Is Not Enough'}
                                </span>
                              </Badge>
                            )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </section>

      <ModalAlert
        title='Remove Active Promotion'
        description='This item currently has an active promotion with specific requirements. If you continue, the promotion will be removed. Are you sure you want to proceed?'
        openModal={modalConfirmSpecific.isShow}
        setOpenModal={(open) => {
          if (!open) {
            modalConfirmSpecific.data?.onCancel?.();
          }
          modalConfirmSpecific.setIsShow(open);
        }}
        onConfirm={modalConfirmSpecific.data?.onConfirm}
      />
    </>
  );
}
